/**
 * BugReportView — Signaler un bug
 * Form: title, description, category. Auto-captures device info.
 */
import React, { useState } from 'react';
import { Button } from '../../../components/ui/button';
import { Loader2, CheckCircle, Send } from 'lucide-react';
import axios from 'axios';
import config from 'config';

const API = `${config.API_URL}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

const CATEGORIES = [
  { value: 'mission', label: 'Mission' },
  { value: 'payment', label: 'Paiement' },
  { value: 'app', label: 'Application' },
  { value: 'other', label: 'Autre' },
];

export default function BugReportView({ user, onBack }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('app');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim()) { setError('Titre et description requis'); return; }
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/driver/report-bug`, {
        title: title.trim(),
        description: description.trim(),
        category,
        device_info: {
          platform: 'web',
          version: navigator.userAgent,
          app_version: 'PWA 1.0',
        },
      }, { headers: getHeaders() });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-12" data-testid="bug-report-success">
        <CheckCircle className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
        <p className="text-white font-semibold mb-1">Bug signale</p>
        <p className="text-white/40 text-sm mb-4">Merci pour votre retour. Notre equipe va traiter votre signalement.</p>
        <Button onClick={onBack} variant="outline" className="border-white/10 text-white">Retour</Button>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="bug-report-form">
      <div>
        <label className="text-white/40 text-xs mb-1 block">Categorie</label>
        <select value={category} onChange={e => setCategory(e.target.value)}
          className="w-full bg-[#111] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:border-[#C9A24D]/50 focus:outline-none"
          data-testid="bug-category-select">
          {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
        </select>
      </div>
      <div>
        <label className="text-white/40 text-xs mb-1 block">Titre</label>
        <input type="text" value={title} onChange={e => setTitle(e.target.value)}
          placeholder="Decrivez brievement le probleme"
          className="w-full bg-[#111] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:border-[#C9A24D]/50 focus:outline-none"
          data-testid="bug-title-input" />
      </div>
      <div>
        <label className="text-white/40 text-xs mb-1 block">Description</label>
        <textarea value={description} onChange={e => setDescription(e.target.value)}
          rows={5} placeholder="Decrivez le bug en detail: etapes pour reproduire, comportement attendu..."
          className="w-full bg-[#111] border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:border-[#C9A24D]/50 focus:outline-none resize-none"
          data-testid="bug-description-input" />
      </div>

      {error && <p className="text-red-400 text-xs">{error}</p>}

      <Button onClick={handleSubmit} disabled={submitting || !title.trim() || !description.trim()}
        className="w-full bg-[#C9A24D] hover:bg-[#E6C77A] text-black font-semibold min-h-[48px]"
        data-testid="submit-bug-btn">
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" />Envoyer</>}
      </Button>
    </div>
  );
}
